/** @module csi/comm/Request */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/cookie",
	"dojo/errors/RequestTimeoutError",
	"dojo/has",
	"dojo/request/xhr",
	"dojo/topic",
	"csi",
	"csi/_base/Deferred",
	"csi/_base/underscore",
	"csi/comm/registry",
	"csi/constants",
	"csi/keycloak/KeycloakAdapter",
	"csi/locale",
	"csi/sessionConstants",
	"csi/xml",
	"./JsonResponse",
	"./Response"
], function(declare, lang, cookie, RequestTimeoutError, has, xhr, topic, csi, Deferred, _, { getRequestPrerequisitePromise }, constants, KeycloakAdapter, locale, sessionConstants, xml, JsonResponse, Response) {

	const ERR_GATEWAY_TIMEOUT = 504;

	const {
		CREW_SCOPE,
		DEP_PATH,
		FMT_AC_RIGHTS,
		FMT_ATTR_INFO,
		FMT_CREW,
		FMT_VERSION,
		FMT_VERSION_VALUE,
		MODULE,
		MODULE_SEARCH,
		OPERATION,
		OUTPUT_JSON,
	} = constants;

	return declare(null, {
		statics: {
			/** indicates if request adds parameter to add crew roles to the response */
			addCrews: false,

			/** indicates if request should bypass query accelerator (because query immediately follows change) */
			disableQueryAccelerator: false,
		},

		/**
		 * @property {boolean} allowRetry - set to true if request can be retried; only do so for idempotent operations that
		 * would return the same result if applied multiple times (such as queries)
		 */
		allowRetry: false,

		urlBase: null,
		content: null,

		constructor: function(/*String?*/module, /*String?*/operation, /*Object?*/params) {
			// provide default format version for response
			this.content = {
				[FMT_VERSION]: FMT_VERSION_VALUE,
			};

			params = params || {};

			if (module && operation) {
				params[MODULE] = module;
				params[OPERATION] = operation;

				if (params[OUTPUT_JSON] == null) {
					params[OUTPUT_JSON] = 2;
				}
			}

			// add standard parameters for search module like crew information and formatting
			if (module == MODULE_SEARCH) {
				this.addNodeCrewParameters();
			}

			// Include default mandatory parameters for operations, such as access rights and attribute info
			this.addParameterIfMissing(FMT_AC_RIGHTS, "Current");
			this.addParameterIfMissing(FMT_ATTR_INFO, "Mid");

			this.urlBase = (module && !operation) ? module : csi.requestUrl();
			this.mixinParameters(params);
		},

		/**
		 * Add crew scope and formatting for operations returning nodes
		 * @return {Request}
		 */
		addNodeCrewParameters: function() {
			if (sessionConstants.admin) {
				this.addParameterIfMissing(CREW_SCOPE, "Result");
				this.addParameterIfMissing(FMT_CREW, "Name");
			}

			return this;
		},

		addDependencyPaths: function(paths) {
			this.addParameter(DEP_PATH, paths, true);
			return this;
		},

		addParameter: function(key, value, bAllowDuplicate) {
			if (value == null) {
				return this;
			}

			var v = this.content[key];

			if (bAllowDuplicate && (v != null)) {
				value = _.flatten([v, value]);
			}

			this.content[key] = value;
			return this;
		},

		addParameterIfMissing: function(key, value) {
			if (!this.hasParameter(key)) {
				this.addParameter(key, value);
			}

			return this;
		},

		hasParameter: function(key) {
			return (this.content[key] != null);
		},

		getParameter: function(key) {
			return this.content[key];
		},

		mixinParameters: function(/*Object*/params) {
			// summary: mixin passed parameters with existing ones (this will overwrite any that already exist)
			if (params) {
				lang.mixin(this.content, params);
			}
			return this;
		},

		removeParameter: function(key) {
			delete this.content[key];
			return this;
		},

		_optimize: function() {
			// summary: reduce list of dependency paths to those necessary for query - for example, query for
			// path of "Child:__Parent__/Child:__Parent__" will satisfy both that path and "Child:__Parent__" alone
			var paths = this.content[DEP_PATH];
			if (!paths) return;

			if (_.isString(paths)) {
				paths = [paths];
			}

			var pathsFinal = [];
			var obj = {};
			var pShort, pLong;

			_.each(paths, function(pNew) {
				if (!pNew || obj[pNew]) {
					return;
				}

				obj[pNew] = true;

				var bFound = _.some(pathsFinal, function(pOld, i) {
					if (pOld.indexOf(pNew + "/") == 0) {
						// path already covered, so stop unless pNew is path for entire map that will supersede paths for specific indices
						pShort = pNew;
						pLong = pOld;
					} else if (pNew.indexOf(pOld + "/") == 0) {
						// replace old path with new path unless index path
						pShort = pOld;
						pLong = pNew;
					} else {
						pShort = null;
					}

					if (pShort) {
						// if paths overlap, then determine if one overrides the other or if both are needed
						var steps = xml.pathGetSteps(pLong.substring(pShort.length + 1));

						if (steps.length && (xml.pathGetStepOperator(steps[0]) == xml.PATH_OP_INDEX)) {
							// if longer path is only longer because of index step into map, then shorter path wins
							if (steps.length == 1) {
								obj[pLong] = false;
								return (pLong == pNew);
							}
						} else if (pLong == pNew) {
							// if longer path is new, then it will replace shorter path
							pathsFinal[i] = pLong;
							return true;
						}
					}

					return false;
				});

				if (!bFound) {
					pathsFinal.push(pNew);
				}
			});

			// only request paths that were not superseded
			this.content[DEP_PATH] = pathsFinal.filter((p) => obj[p]);
		},

		_splitRequest: function(/*Object*/ioArgs) {
			// summary: split arguments into array of arguments if request is too large so that it can be processed in chunks
			return null;
		},

		_toXmlRequest: function(/*Object*/ioArgs) {
			// summary: convert request into XML - only used for logging
			this._optimize();

			var content = this.content;
			var module = content.Module;
			var operation = content.Operation;
			if (!module || !operation) return ioArgs.data;

			var omit = {
				Module: true,
				Operation: true,
				OutputJSON: true
			};

			var s = '<Request Module="' + module + '" Operation="' + operation + '">\r\n';
			var x, y;

			function addParam(name, value) {
				s += '<Param Name="' + name + '" Value="' + value + '" />\r\n';
			}

			for (x in content) {
				if (omit[x]) continue;

				y = content[x];

				if (y instanceof Array) {
					_.each(y, function(z) { addParam(x, z); });
				} else if (y != null) {
					addParam(x, y);
				}
			}

			// output binary if used
			if (this.binaryXml) {
				s += '<Param File="true" Name="' + this.binaryKey + '" Value="FILE NAME" />\r\n</Request>\r\n\r\nFILE CONTENT:\r\n' + this.binaryXml;
			} else {
				s += '</Request>';
			}

			return s;
		},

		_xhr: function(/*Object*/ioArgs, /*Boolean*/bSplit) {
			// summary: core AJAX method only supporting asynchronous requests
			const promise = getRequestPrerequisitePromise();
			const dfd = new Deferred();

			// handle old dojo/_base/xhr interface
			if (ioArgs.load || ioArgs.error) {
				dfd.then(ioArgs.load, ioArgs.error);
				ioArgs.load = ioArgs.error = null;
			}

			// prevent caching unless explicitly told otherwise
			if (ioArgs.preventCache !== false) {
				ioArgs.preventCache = true;
			}

			// handle as javascript unless using true Json output from server
			if (!ioArgs.handleAs) {
				ioArgs.handleAs = (this.getParameter(OUTPUT_JSON) === 2) ? "json" : "javascript";
			}

			// set retry count if allowed and not already set
			ioArgs.retryCount = ioArgs.retryCount || (this.allowRetry ? 3 : 0);

			// make 30 minutes default timeout
			ioArgs.timeout = ioArgs.timeout || (1000 * 60 * 30);

			// track performance of request
			dfd.startTimer();

			// handle sync request
			if (ioArgs.sync) {
				ioArgs._csiDfd = dfd;
				this._xhrRequest(ioArgs);
				return ioArgs._csiResponse;
			}

			// wait for response from any held request before issuing new request;
			// don't need to worry about error handling as the global error handler for the
			// hold service will take care of it
			promise.then(() => {
				const splitArgs = bSplit ? null : this._splitRequest(ioArgs);
				if (splitArgs) {
					// issue split requests
					this._xhrSplit(splitArgs, dfd);
				} else {
					// issue request
					ioArgs._csiDfd = dfd;
					this._xhrRequest(ioArgs);
				}
			});

			return dfd;
		},

		_xhrRequest: function(ioArgs) {
			this._addBearerToken(ioArgs);
			this._addCsrfToken(ioArgs);
			this._addCustomHeaders(ioArgs);

			//summary: issue request
			var dfd = ioArgs._csiDfd;
			var xhrResult;
			var self = this;
			dfd.reqDfd = xhr(ioArgs.url, ioArgs);
			dfd.reqDfd.then(
				function(res) {
					return dfd.reqDfd.response.then(function(result) {
						xhrResult = result;
						return res;
					});
				}
			).then(
				function(res) {
					return self._xhrLoad(ioArgs, res, xhrResult);
				},
				lang.hitch(this, this._xhrError, ioArgs)
			);
		},

		/**
		 * Adds the Bearer token Authorization header to the request if authenticated with Keycloak
		 * @param {Object} ioArgs - The ioArgs object
		 */
		_addBearerToken(ioArgs) {
			const bearerToken = KeycloakAdapter.getTokenSync();

			if (bearerToken) {
				this._addHeader(ioArgs, "Authorization", "Bearer " + bearerToken);
			}
		},


		/**
		 * Adds the X-CSRF-Token header to the request
		 * @param {Object} ioArgs - The ioArgs object
		 */
		_addCsrfToken(ioArgs) {
			const csrfToken = cookie('CSRFToken');

			if (csrfToken) {
				this._addHeader(ioArgs, "X-CSRF-Token", csrfToken);
			}
		},

		/**
		 * Adds the custom headers to the request
		 * @param {Object} ioArgs - The ioArgs object
		 */
		_addCustomHeaders(ioArgs) {
			var self = this;
			const customHeaders = sessionConstants ? sessionConstants.customRequestHeaders : [];
			_.each(customHeaders, function(header) {
				const nameVal = header.split(",");
				self._addHeader(ioArgs, nameVal[0], nameVal[1]);
			});
		},

		/**
		 * Adds a header to the ioArgs attribute.
		 * If called multiple times with the same headerName, the previous value will be overwritten.
		 * @param {Object} ioArgs - The ioArgs object
		 * @param {string} headerName - The header name to be added to the request
		 * @param {string} headerValue - The header value to be added to the request
		 */
		_addHeader(ioArgs, headerName, headerValue) {
			if (ioArgs.headers) {
				ioArgs.headers[headerName] = headerValue;
			} else {
				ioArgs.headers = { [headerName]: headerValue }
			}
		},

		_xhrEnd: function(/*Object*/ioArgs, /*Object?*/res) {
			// summary: log request and cleanup
			var dfd = new Deferred();
			var dfdArg = ioArgs._csiDfd;
			ioArgs._csiDfd = null;
			dfdArg.endTimer();

			if (has("csi-track-xhr")) {
				try {
					// the dfd response holds the real result with all parameters (text, xhr, etc.), so this
					// is the only way to get to that result; need to delay processing from continuing until
					// that is done or else tracking will not get notified of the request before the action tracking ends
					var req = this._toXmlRequest(ioArgs);
					var reqContent = this.content;

					dfdArg.reqDfd.response.then(function(result) {
						topic.publish("/csi/xhr/end", dfdArg, req, result.text, result.xhr, reqContent);
						dfd.resolve(dfdArg);
					});

					return dfd;
				} catch (ex) {
					// IE throws exception in some cases accessing responseText, so trap here
					// FIX - not sure if still valid with new dojo/request module, but can't hurt
				}
			}

			dfd.resolve(dfdArg);
			return dfd;
		},

		_xhrError: function(/*Object*/ioArgs, /*Error*/err) {
			// summary: handle request error
			console.warn(err);
			this._xhrHandleError(ioArgs, err);
		},

		_xhrLoad: function(/*Object*/ioArgs, /*Object*/res, /*Object*/xhrResult) {
			// summary: handle request response
			if (res == null) {
				// response is empty
				console.warn("XmlHttpRequest: empty response");

				if (--ioArgs.retryCount) {
					// retry request if under count
					this._xhrRequest(ioArgs);
				} else {
					this._xhrHandleError(ioArgs);
				}
				return;
			}

			this._xhrEnd(ioArgs, res).then(function(dfd) {
				var responseClass = (ioArgs.handleAs == "javascript" || ioArgs.handleAs == "json") ? JsonResponse : Response;
				var response = new responseClass(res, xhrResult);

				// store current server time
				var time = response.getTime();
				if (time) {
					csi.setCurrentTime(time);
				}

				// handle errors
				var err = response.getError();
				if (err) {
					csi.error(dfd, err, response.isConnectionError());
					return;
				}

				// store response for sync requests to be returned from _xhr function
				if (ioArgs.sync) {
					ioArgs._csiResponse = response;
				}

				// call original load function
				dfd.resolve(response);
			});
		},

		_xhrHandleError: function(/*Object*/ioArgs, /*Error?*/err) {
			// summary: handle request error
			let bConnectionError;

			if (err) {
				const s = err.response && err.response.status;
				const m = err.message;

				if ((err instanceof RequestTimeoutError) || (s == ERR_GATEWAY_TIMEOUT)) {
					// timeout error which should cause a page reload - don't send Deferred
					// to error handler as there is no way to recover gracefully from timeout
					this._xhrEnd(ioArgs).then((dfd) => {
						csi.error(null, new Error(locale.resource("SERVER_TIMEOUT")), false);
					});

					return;
				}

				if ((s == 404) || (s == 503) || (m && ((m.indexOf("syntax error") >= 0) || (m.indexOf(":404") >= 0) || (m.indexOf(":503") >= 0)))) {
					// throw error - replace 404/503 errors with more user-friendly error
					err = null;
					bConnectionError = true;
				}
			}

			this._xhrEnd(ioArgs).then((dfd) => {
				csi.error(dfd, err || new Error(locale.resource("SERVER_UNRESPONSIVE")), bConnectionError);
			});
		},

		/**
		 * Issue multiple requests in succession to fulfill large request
		 * @param {Object[]} splitArgs
		 * @param {Deferred} dfd - resolve when all requests complete
		 */
		_xhrSplit: function(splitArgs, dfd) {
			var self = this;
			var responses = [];
			var i = 0;

			function done() {
				// merge responses into one
				var response;

				_.each(responses, function(res) {
					if (response) {
						response.mergeResponse(res);
					} else {
						response = res;
					}
				});

				dfd.resolve(response);
			}

			function go() {
				var ioArgs = splitArgs[i++];
				if (ioArgs) {
					self._xhr(ioArgs, true).then(
						function onLoad(response) {
							responses.push(response);

							if (ioArgs.bTestSplitResults && !response.hasMoreResults()) {
								// if no more results, then no need to continue
								done();
							} else if (!dfd.isCanceled()) {
								// continue until all requests are issued so long as original deferred was not canceled
								go();
							}
						},
						function onError(err) {
							dfd.reject(err);
						}
					);
				} else {
					done();
				}
			}

			go();
		}
	});
});